/**
 * screens/jobs/jobDetail.tsx
 * Job (requisition) detail — wired to GET /api/hire/v2/requisitions/:id.
 * Header + status + client-ref chip · Activate (draft) / Run match (active) ·
 * matching criteria (current revision, immutable) · revision history timeline.
 * Pipeline kanban + matched candidates arrive with the actions/match slices.
 */
import React, { useCallback, useState } from 'react';
import { View, Text, ScrollView, Pressable, ActivityIndicator, StyleSheet, Platform } from 'react-native';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { useTheme } from '../../context/themeContext';
import { useBreakpoints, ContentMaxWidth } from '../../theme/responsive';
import { web } from '../../utils/web';
import { useHover } from '../../hooks/useHover';
import { ActionButton, StatusChip, Panel, Icon } from '../../components/ui';
import { SkillChips } from '../../components/hire/badges';
import { getRequisition, activateRequisition, reopenRequisition, closeRequisition, downloadRequisitionSourceFile } from '../../services/hire/requisitionV2';
import { getPipeline, recordAction, getLatestRun, runMatch } from '../../services/hire/matchV2';
import type { RunStaleness } from '../../services/hire/matchV2';
import { Alert } from '../../components/alert';
import type { RequisitionDetail } from '../../schemas/hire/requisitionV2';
import type { Pipeline, ActionType, MatchRunDetail } from '../../schemas/hire/matchV2';
import { MiniAvatar } from '../../components/hire/badges';
import ReasonModal from '../../components/hire/reasonModal';
// Edit the job in place — same create/edit form used by the Jobs list.
import NewJobModal from '../../components/hire/newJobModal';
import ConfirmDialog from '../../components/confirmDialog';
import SuccessDialog from '../../components/successDialog';
import { showToast } from '../../components/toastComponent';
import { getApiErrorMessage } from '../../utils/errorHandler';
import { skillsOnlyBreakdown } from '../../utils/hireFormat';
import type { AppStackParamList } from '../../navigation/types';
import { reqStatusVM, expBand, REMOTE_LABEL } from './jobsVm';
import { toneColors } from '../../theme/tone';

/** Premium staggered entrance (web-only; inert on native). */
function rise(delay: number) {
  return web({ animation: 'sk-rise 480ms cubic-bezier(.2,.7,.2,1) both', animationDelay: `${delay}ms` });
}

/** The next forward stage for each lane (append-only advance). */
const NEXT_STAGE: Record<string, ActionType | undefined> = {
  shortlisted: 'submitted_to_client',
  submitted_to_client: 'interview_scheduled',
  interview_scheduled: 'offered',
  offered: 'placed',
};

/** Human label for each advance target (for the confirm dialog). */
const ADVANCE_LABEL: Record<string, string> = {
  submitted_to_client: 'Submitted',
  interview_scheduled: 'Interview',
  offered: 'Offer',
  placed: 'Placed',
};

/** Compact button used on pipeline cards. */
function PipeBtn({ label, tone, onPress, busy, flex }: { label: string; tone: 'accent' | 'danger' | 'neutral'; onPress: () => void; busy?: boolean; flex?: boolean }) {
  const { theme } = useTheme();
  const { colors, fontWeight, radii } = theme;
  const bg = tone === 'accent' ? colors.accent : tone === 'danger' ? colors.dangerSoft : colors.paper2;
  const fg = tone === 'accent' ? colors.onAccent : tone === 'danger' ? colors.danger : colors.ink2;
  const border = tone === 'accent' ? colors.accent : colors.line;
  return (
    <Pressable
      onPress={busy ? undefined : onPress}
      style={[{ flex: flex ? 1 : undefined, height: 24, borderRadius: radii.sm, borderWidth: 1, borderColor: border, backgroundColor: bg, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 6, opacity: busy ? 0.6 : 1 }, web({ cursor: busy ? 'default' : 'pointer' })]}
    >
      <Text numberOfLines={1} style={{ fontSize: 10, fontWeight: fontWeight.medium, color: fg }}>{busy ? '…' : label}</Text>
    </Pressable>
  );
}

/** Revision-history row with a timeline rail + hover highlight. */
function RevRow({
  revNo, title, dateLabel, isCurrent, isLast, onHoverStyle,
}: { revNo: number; title: string; dateLabel: string; isCurrent: boolean; isLast: boolean; onHoverStyle?: object }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font, radii } = theme;
  const { hovered, hoverProps } = useHover();
  return (
    <View {...hoverProps} style={[{ flexDirection: 'row', gap: 13, borderRadius: radii.md, paddingVertical: 12, paddingHorizontal: 10, backgroundColor: hovered ? colors.paper2 : 'transparent' }, web({ transition: 'background 130ms ease' }), onHoverStyle]}>
      {/* Timeline rail: dot (aligned to the title line) + connector to the next row. */}
      <View style={{ alignItems: 'center', width: 10 }}>
        <View style={{ width: 10, height: 10, borderRadius: 999, backgroundColor: isCurrent ? colors.accent : colors.card, borderWidth: isCurrent ? 0 : 2, borderColor: colors.ink4, marginTop: 5, ...(isCurrent && Platform.OS === 'web' ? ({ boxShadow: `0 0 0 3px ${colors.accentSoft}` } as any) : {}) }} />
        {!isLast && <View style={{ flex: 1, width: 1.5, marginTop: 6, marginBottom: 2, backgroundColor: colors.line }} />}
      </View>
      <View style={{ flex: 1, minWidth: 0, paddingBottom: isLast ? 0 : 6 }}>
        {/* Title-led: the revision's job title reads first; rev no + date form a
            quiet meta line beneath. */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <Text numberOfLines={1} style={{ flex: 1, minWidth: 0, fontSize: fontSize.md, color: colors.ink, fontWeight: fontWeight.semibold, letterSpacing: -0.1 }}>{title}</Text>
          {isCurrent && <StatusChip tone="accent" label="current" dot={false} />}
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 6 }}>
          <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, color: colors.ink3, fontWeight: fontWeight.semibold }}>rev {revNo}</Text>
          <View style={{ width: 3, height: 3, borderRadius: 999, backgroundColor: colors.ink4 }} />
          <Text style={{ fontSize: fontSize.xs, color: colors.ink4 }}>{dateLabel}</Text>
        </View>
      </View>
    </View>
  );
}

/** Pipeline candidate card with hover lift. */
function PipeCard({
  name, terminal, next, moving, onOpen, onAdvance, onReject, onWithdraw,
}: {
  name: string; terminal: boolean; next: ActionType | undefined; moving: boolean;
  onOpen: () => void; onAdvance: () => void; onReject: () => void; onWithdraw: () => void;
}) {
  const { theme } = useTheme();
  const { colors, fontSize } = theme;
  const { hovered, hoverProps } = useHover();
  return (
    <View
      {...hoverProps}
      style={[
        { backgroundColor: colors.card, borderWidth: 1, borderColor: hovered ? colors.lineStrong : colors.line, borderRadius: 10, padding: 9, gap: 7 },
        web({ transition: 'border-color 140ms ease, transform 140ms ease, box-shadow 140ms ease' }),
        hovered && web({ transform: 'translateY(-2px)', boxShadow: '0 8px 20px -12px rgba(0,0,0,0.3)' }),
      ]}
    >
      <Pressable onPress={onOpen} style={[{ flexDirection: 'row', alignItems: 'center', gap: 8 }, web({ cursor: 'pointer' })]}>
        <MiniAvatar name={name} size={24} />
        <Text numberOfLines={1} style={{ fontSize: fontSize.xs, color: colors.ink, flex: 1, fontWeight: '500' }}>{name}</Text>
      </Pressable>
      {!terminal && (
        <View style={{ gap: 5 }}>
          {next && <PipeBtn label="Advance →" tone="accent" busy={moving} onPress={onAdvance} />}
          <View style={{ flexDirection: 'row', gap: 5 }}>
            <PipeBtn label="Reject" tone="danger" flex onPress={onReject} />
            <PipeBtn label="Withdraw" tone="neutral" flex onPress={onWithdraw} />
          </View>
        </View>
      )}
    </View>
  );
}

const LANES: { key: string; label: string; tone: 'info' | 'accent' | 'purple' | 'warn' | 'success' | 'neutral' }[] = [
  { key: 'shortlisted', label: 'Shortlisted', tone: 'info' },
  { key: 'submitted_to_client', label: 'Submitted', tone: 'accent' },
  { key: 'interview_scheduled', label: 'Interview', tone: 'purple' },
  { key: 'offered', label: 'Offer', tone: 'warn' },
  { key: 'placed', label: 'Placed', tone: 'success' },
  // No 'closed' lane. It held rejected + withdrawn candidates, and sitting at the end
  // of a forward-moving board it read as a STAGE — the step after Placed — when it is
  // the opposite: the people who left. It also collided with the job's own 'closed'
  // status, so "Closed" on this screen meant two unrelated things depending on where
  // you looked. The board now shows only the forward pipeline.
  //
  // The API still returns `lanes.closed`, so nothing broke server-side and restoring
  // this is one line. Trade-off worth knowing: rejected and withdrawn candidates are
  // not visible on this board any more — their actions are still recorded and still
  // reachable per candidate, but the board no longer surfaces them.
];

/**
 * Token history is HIDDEN, not removed.
 *
 * The screen, its route and its API all still work — `jobTokenHistory` remains
 * navigable, so a saved link or a future entry point keeps functioning. Only the
 * button on the job header is withheld. Flip this to true to bring it back.
 */
const SHOW_TOKEN_HISTORY = false;

type Nav = NativeStackNavigationProp<AppStackParamList>;
type Rt = RouteProp<AppStackParamList, 'jobDetail'>;

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}
function money(min: number | null, max: number | null, ccy: string): string {
  if (min == null && max == null) return 'Not specified';
  const f = (n: number) => `${ccy} ${n.toLocaleString()}`;
  if (min != null && max != null) return `${f(min)} – ${f(max)}`;
  return f((min ?? max) as number);
}

export default function JobDetailScreen() {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font, radii } = theme;
  const { isMobile } = useBreakpoints();
  const navigation = useNavigation<Nav>();
  const { params } = useRoute<Rt>();
  const id = params.id;

  const [data, setData] = useState<RequisitionDetail | null>(null);
  const [pipeline, setPipeline] = useState<Pipeline | null>(null);
  // Latest STORED match run shown inline. Loading it is read-only and free —
  // opening a job must never spend rerank tokens to reproduce a known ranking.
  const [match, setMatch] = useState<MatchRunDetail | null>(null);
  const [staleness, setStaleness] = useState<RunStaleness | null>(null);
  const [matchLoading, setMatchLoading] = useState(true);
  const [rerunning, setRerunning] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  // Asked immediately after an edit saves: a revision invalidates every stored
  // score, so the moment to offer the re-run is now — not whenever the recruiter
  // happens to notice the banner.
  const [askRerun, setAskRerun] = useState(false);
  const [busy, setBusy] = useState(false);
  const [movingId, setMovingId] = useState<string | null>(null);
  const [reasonFor, setReasonFor] = useState<{ candidateId: string; action: 'rejected' | 'withdrawn' } | null>(null);
  const [confirmActivate, setConfirmActivate] = useState(false);
  const [confirmReopen, setConfirmReopen] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [activated, setActivated] = useState(false); // success dialog
  const [loadingSource, setLoadingSource] = useState(false);
  const [downloadingSource, setDownloadingSource] = useState(false);

  // Save the stored source document to disk (scan-gated server-side). The bytes
  // are streamed through our own API rather than the Drive signed URL: that URL
  // is cross-origin with no CORS headers, so the browser cannot read it as a
  // blob and an anchor to it renders the PDF in a tab instead of saving it.
  const onDownloadSource = async () => {
    setDownloadingSource(true);
    try {
      const blob = await downloadRequisitionSourceFile(id);
      const name = data?.sourceFilename ?? 'job-source-document';
      if (Platform.OS === 'web') {
        const doc: any = (globalThis as any).document;
        const blobUrl = URL.createObjectURL(blob);
        const a = doc.createElement('a');
        a.href = blobUrl;
        a.download = name;
        doc.body.appendChild(a);
        a.click();
        a.remove();
        // Revoke on the next tick — tearing the object URL down synchronously
        // can cancel the download before the browser has read it.
        setTimeout(() => URL.revokeObjectURL(blobUrl), 0);
      }
    } catch (e) {
      showToast('error', 'Could not download the document', getApiErrorMessage(e, 'The file may still be scanning. Try again shortly.'));
    } finally {
      setDownloadingSource(false);
    }
  };

  // Fetch a signed URL for the stored original and open it (scan-gated server-side).
  const onViewSource = async () => {
    setLoadingSource(true);
    // Open the tab synchronously inside the click gesture — a window.open after
    // an await is treated as non-user-initiated and blocked. `noopener` is not
    // usable here (we need the handle to navigate), so `opener` is nulled to keep
    // reverse-tabnabbing shut.
    const win = Platform.OS === 'web' ? (globalThis as any).open?.('about:blank', '_blank') : null;
    if (win) win.opener = null;
    try {
      // Fetch the BYTES through our own API and show them as a blob URL, rather
      // than sending the browser to the SK-Drive signed URL. Two reasons:
      //   • the signed URL is cross-origin with no CORS headers, so the tab is at
      //     the mercy of Drive's own response — and a slow or refused fetch there
      //     left a blank tab open with nothing to explain it
      //   • the server already streams these bytes for Download, and that path
      //     reuses the cached clean-scan verdict, so View costs one call instead
      //     of its own scan + sign round trip
      const blob = await downloadRequisitionSourceFile(id);
      const blobUrl = URL.createObjectURL(blob);
      if (win) win.location.href = blobUrl;
      else if (Platform.OS === 'web') (globalThis as any).open?.(blobUrl, '_blank', 'noopener');
      // Revoked late: the tab needs the URL alive long enough to load it.
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (e) {
      if (win) win.close();
      showToast('error', 'Could not open the document', getApiErrorMessage(e, 'The file may still be scanning, or SK Drive is unreachable. Try again shortly.'));
    } finally {
      setLoadingSource(false);
    }
  };
  const [pendingAdvance, setPendingAdvance] = useState<{ candidateId: string; name: string; next: ActionType; nextLabel: string } | null>(null);

  const load = useCallback(() => {
    let active = true;
    getRequisition(id).then((d) => { if (active) setData(d); }).catch(() => { if (active) setData(null); });
    getPipeline(id).then((p) => { if (active) setPipeline(p); }).catch(() => { if (active) setPipeline(null); });
    setMatchLoading(true);
    getLatestRun(id)
      .then((r) => { if (active) { setMatch(r.run); setStaleness(r.staleness); } })
      .catch(() => { if (active) { setMatch(null); setStaleness(null); } })
      .finally(() => { if (active) setMatchLoading(false); });
    return () => { active = false; };
  }, [id]);
  useFocusEffect(load);

  const refreshPipeline = () => getPipeline(id).then(setPipeline).catch(() => {});

  /**
   * The single source of truth for "these results are out of date", rendered in
   * both the header and the Matches card. Derived rather than duplicated so the
   * two can never disagree — a job edit invalidates everything, candidate changes
   * only add to it, and a job edit is reported first because it is the wider
   * problem.
   */
  const staleTags: { label: string; tone: 'warn' | 'accent' }[] = (() => {
    if (!staleness || staleness.fresh) return [];
    const out: { label: string; tone: 'warn' | 'accent' }[] = [];
    // Three independent facts, reported independently — collapsing them is how a
    // pool change on an unrelated candidate ended up flagged on every job.
    if (staleness.jobChanged) out.push({ label: 'Job updated · re-run needed', tone: 'warn' });
    const upd = staleness.updatedCandidateIds.length;
    if (upd > 0) {
      out.push({ label: `${upd} ranked candidate${upd === 1 ? '' : 's'} updated · re-run needed`, tone: 'accent' });
    }
    const fresh = staleness.newCandidateIds.length;
    if (fresh > 0) {
      // Deliberately softer wording: these MAY not match this job at all — that is
      // exactly what scoring them decides.
      out.push({ label: `${fresh} new candidate${fresh === 1 ? '' : 's'} to score`, tone: 'accent' });
    }
    return out;
  })();
  /** Everything an incremental run would score for this job. */
  const pendingCount =
    (staleness?.updatedCandidateIds.length ?? 0) + (staleness?.newCandidateIds.length ?? 0);

  /**
   * The only path that (re)scores anything — always an explicit user action.
   * `incremental` scores just the unscored candidates and merges them in, so the
   * ones already judged are left exactly as they are and nothing is charged. A
   * job edit cannot use it: the criteria moved, so every stored score is stale.
   */
  const onRunMatch = async (incremental = false) => {
    setRerunning(true);
    try {
      const run = await runMatch(id, incremental ? 'incremental' : undefined);
      setMatch(run);
      setStaleness(null); // fresh results — nothing left to report
    } catch (e) {
      showToast('error', 'Could not run the match', getApiErrorMessage(e));
    } finally {
      setRerunning(false);
    }
  };

  /**
   * Reopen a closed job. The undo for an automatic close — reopening only moves the
   * status, so every revision, pipeline action and match run survives. Consumes an
   * active-openings slot server-side, so it can legitimately fail on a plan cap;
   * that error is surfaced rather than swallowed.
   */
  const onReopen = async () => {
    setBusy(true);
    try {
      setData(await reopenRequisition(id));
      setConfirmReopen(false);
      showToast('success', 'Job reopened', `“${rev.title}” is active again. Its pipeline and match results are unchanged.`);
    } catch (e) {
      setConfirmReopen(false);
      showToast('error', 'Could not reopen the job', getApiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Close the job by hand.
   *
   * Closing was reachable only from the Jobs LIST, so the screen where a recruiter
   * actually works a pipeline — watching the last candidate reach Placed — had no way
   * to act on it. Automatic closing covers the case where every opening is filled;
   * this covers every other reason a job ends: the role was cancelled, filled
   * elsewhere, or put down. Reversible via Reopen, so it is not a trap.
   */
  const onClose = async () => {
    setBusy(true);
    try {
      setData(await closeRequisition(id));
      setConfirmClose(false);
      showToast('success', 'Job closed', `“${rev.title}” is closed and will stop matching. Reopen it if you need it back.`);
    } catch (e) {
      setConfirmClose(false);
      showToast('error', 'Could not close the job', getApiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const onActivate = async () => {
    setBusy(true);
    try {
      setData(await activateRequisition(id));
      setConfirmActivate(false);
      setActivated(true); // success dialog
    } catch (e) {
      setConfirmActivate(false);
      showToast('error', 'Could not activate the job', getApiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  // Confirmed advance — runs after the user accepts the dialog.
  const doAdvance = async () => {
    if (!pendingAdvance) return;
    const { candidateId, next, nextLabel } = pendingAdvance;
    setMovingId(candidateId);
    try {
      const res = await recordAction({ requisitionId: id, candidateId, action: next });
      setPendingAdvance(null);
      await refreshPipeline();
      if (res.requisitionClosed) {
        // The stage after Placed. The recruiter moved a candidate and the JOB
        // changed state as a result — say so here, rather than leaving them to
        // notice later that it has dropped off their active list. Reload the whole
        // job so the status chip and actions reflect 'closed' immediately.
        await load();
        const seats = res.openingsCount ?? 1;
        showToast(
          'success',
          'All openings filled — job closed',
          `${pendingAdvance.name} was placed. ${seats === 1 ? 'The single opening is' : `All ${seats} openings are`} filled, so this job is now closed. Reopen it if you are still hiring.`,
        );
      } else {
        showToast('success', `Moved to ${nextLabel}`, `${pendingAdvance.name} is now in ${nextLabel}.`);
      }
    } catch (e) {
      setPendingAdvance(null);
      showToast('error', 'Could not move the candidate', getApiErrorMessage(e));
    } finally {
      setMovingId(null);
    }
  };

  const submitReason = async (reason: string) => {
    if (!reasonFor) return;
    await recordAction({ requisitionId: id, candidateId: reasonFor.candidateId, action: reasonFor.action, notes: reason });
    setReasonFor(null);
    await refreshPipeline();
  };

  if (!data) {
    return <View style={styles.loading}><ActivityIndicator size="large" color={colors.accent} /></View>;
  }

  const vm = reqStatusVM(data.status);
  const rev = data.revision;

  const META: { label: string; value: string }[] = [
    { label: 'Experience', value: expBand(rev.experienceMin, rev.experienceMax) },
    { label: 'Salary band', value: money(rev.salaryMin, rev.salaryMax, rev.salaryCurrency) },
    { label: 'Notice ceiling', value: rev.noticePeriodMaxDays != null ? `${rev.noticePeriodMaxDays} days` : 'Any' },
    { label: 'Locations', value: rev.locations.length ? rev.locations.join(', ') : 'Any' },
    { label: 'Remote policy', value: REMOTE_LABEL[rev.remotePolicy] },
    { label: 'Education', value: rev.educationMin || 'Any' },
  ];

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: isMobile ? 18 : 32, paddingBottom: 72 }}>
      <View style={{ width: '100%', maxWidth: ContentMaxWidth.default, alignSelf: 'center', gap: 24 }}>
        {/* Back */}
        <Pressable onPress={() => navigation.navigate('jobs')} style={[{ flexDirection: 'row', alignItems: 'center', gap: 5, alignSelf: 'flex-start' }, rise(0), web({ cursor: 'pointer' })]}>
          <Icon name="chev-l" size={15} color={colors.ink3} />
          <Text style={{ fontSize: fontSize.sm, color: colors.ink2, fontWeight: fontWeight.medium }}>Jobs</Text>
        </Pressable>

        {/* Header — title wraps + shrinks so long JDs never cause horizontal scroll. */}
        <View style={[styles.header, rise(60), isMobile && { flexDirection: 'column', alignItems: 'flex-start', gap: 14 }]}>
          <View style={{ gap: 8, flex: 1, minWidth: 0 }}>
            <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
              <Text style={{ fontSize: isMobile ? 22 : 26, fontWeight: fontWeight.bold, color: colors.ink, letterSpacing: -0.6, flexShrink: 1, lineHeight: isMobile ? 28 : 32 }}>{rev.title}</Text>
              <View style={{ marginTop: 4 }}><StatusChip tone={vm.tone} label={vm.label} /></View>
              {/* Sticks in the header from the moment a revision is saved until a
                  re-run clears it. The Matches panel already says so, but that is
                  below the fold on a long job — this makes "these results predate
                  the current criteria" visible wherever you are on the screen. */}
              {staleTags.map((t) => (
                <StaleTag key={t.label} label={t.label} tone={t.tone} style={{ marginTop: 4 }} />
              ))}
            </View>
            <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, color: colors.ink4, letterSpacing: 0.2 }}>
              {data.code} · rev {data.currentRevision} · {rev.openingsCount} opening{rev.openingsCount === 1 ? '' : 's'} · {fmtDate(data.createdAt)}
            </Text>
          </View>
          <View style={[{ flexDirection: 'row', gap: 10 }, isMobile ? { alignSelf: 'stretch' } : { flexWrap: 'wrap' }]}>
            {SHOW_TOKEN_HISTORY && (
              <View style={isMobile ? { flex: 1 } : undefined}>
                <ActionButton label="Token history" variant="secondary" icon="chart" onPress={() => navigation.navigate('jobTokenHistory', { id })} />
              </View>
            )}
            {data.status === 'draft' ? (
              <View style={isMobile ? { flex: 1 } : undefined}>
                <ActionButton label={isMobile ? 'Activate JD' : 'Activate & embed JD'} variant="success" icon="check" onPress={() => setConfirmActivate(true)} loading={busy} />
              </View>
            ) : data.status === 'active' || data.status === 'on_hold' ? (
              <>
                {data.status === 'active' && (
                  <View style={isMobile ? { flex: 1 } : undefined}>
                    <ActionButton label="Run match" variant="primary" icon="play" onPress={() => navigation.navigate('run', { requisitionId: id, autoRun: Date.now() })} />
                  </View>
                )}
                <View style={isMobile ? { flex: 1 } : undefined}>
                  <ActionButton label="Close job" variant="secondary" icon="check" onPress={() => setConfirmClose(true)} loading={busy} />
                </View>
              </>
            ) : data.status === 'closed' ? (
              // A closed job had no action at all, which was tolerable while closing
              // was always a deliberate click. Now the last placement closes a job on
              // its own, so there has to be a way back — otherwise one mis-click on
              // "Placed" archives a live requisition and the only recovery is to
              // rebuild it. Reopening keeps every revision, action and match run.
              <View style={isMobile ? { flex: 1 } : undefined}>
                <ActionButton label="Reopen job" variant="secondary" icon="refresh" onPress={() => setConfirmReopen(true)} loading={busy} />
              </View>
            ) : null}
          </View>
        </View>

        {/* Detail strip — Mode + Source grouped tightly so they read as one
            cohesive block, set apart from the criteria panel below. */}
        <View style={{ gap: 12 }}>
        {/* Engagement mode — icon-led card matching the Source-document card so
            the detail strip reads as one consistent, premium group. */}
        <View style={[{ flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 14, paddingHorizontal: 16, borderWidth: 1, borderColor: colors.line, borderRadius: radii.lg, backgroundColor: colors.card }, rise(110), web({ boxShadow: '0 1px 0 rgba(16,24,40,0.04)' })]}>
          <View style={{ width: 38, height: 38, borderRadius: 10, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line }}>
            <Icon name={data.inHouse ? 'layers' : 'users'} size={18} color={colors.ink2} />
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={{ fontSize: fontSize.xs, color: colors.ink3, textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: fontWeight.semibold }}>Mode</Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 4 }}>
              {data.inHouse ? (
                <StatusChip tone="neutral" label="In-house" dot={false} />
              ) : (
                <>
                  <StatusChip tone="accent" label="Client account" dot={false} />
                  <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, color: colors.ink3 }} numberOfLines={1}>{data.clientAccountId}</Text>
                </>
              )}
            </View>
          </View>
        </View>

        {/* Ranked matches, inline. These are the STORED results of the last run —
            shown for free on every visit. Nothing re-scores on open; when the job
            or the pool has moved, the banner says so and the user chooses to
            spend. Deep actions (submit / reject / feedback) live on the run
            screen, reached via "Open full results". */}
        <View style={rise(150)}>
          <Panel
            title={match ? `Matches · ${match.rankedCount} · Strong Match ≥ ${match.cutoff}` : 'Matches'}
            action={
              // No standing "Re-run": with nothing new to score it would only
              // spend tokens to reproduce the same ranking. Re-running is offered
              // by the staleness strip below, and only when there IS something
              // new — a changed job or unscored candidates. The first run for a
              // job is the one exception, since there is nothing to show yet.
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                {!isMobile && staleTags.map((t) => <StaleTag key={t.label} label={t.label} tone={t.tone} />)}
                {!!match && (
                  <ActionButton
                    label="Open full results"
                    variant="secondary"
                    size="sm"
                    icon="arrow-r"
                    onPress={() => navigation.navigate('run', { requisitionId: id, runId: match.id })}
                  />
                )}
                {!match && data.status === 'active' && (
                  <ActionButton
                    label="Run match"
                    variant="primary"
                    size="sm"
                    icon="play"
                    loading={rerunning}
                    onPress={() => void onRunMatch(false)}
                  />
                )}
              </View>
            }
          >
            {matchLoading ? (
              <View style={{ paddingVertical: 24, alignItems: 'center' }}><ActivityIndicator color={colors.accent} /></View>
            ) : !match ? (
              <View style={{ paddingVertical: 20, alignItems: 'center', gap: 8 }}>
                <Icon name="search" size={18} color={colors.ink4} />
                <Text style={{ fontSize: fontSize.sm, color: colors.ink3 }}>
                  No match run yet — run one to see ranked candidates for this job.
                </Text>
              </View>
            ) : (
              <View style={{ gap: 12 }}>
                {/* Out-of-date notice: what changed, and the one button that spends. */}
                {/* Staleness notice — a quiet inline strip, not a shouting block.
                    The action sits INLINE at its natural width: a full-bleed
                    primary button reads as the panel's main call to action, when
                    it is really a small correction offered in passing. */}
                {staleness && !staleness.fresh && (() => {
                  const st = toneColors(colors, staleness.jobChanged ? 'warn' : 'accent');
                  return (
                    <View
                      style={[
                        {
                          flexDirection: isMobile ? 'column' : 'row',
                          alignItems: isMobile ? 'stretch' : 'center',
                          gap: isMobile ? 10 : 14,
                          paddingVertical: 11,
                          paddingHorizontal: 13,
                          borderRadius: radii.md,
                          borderWidth: 1,
                          borderColor: st.border,
                          backgroundColor: st.bg,
                        },
                        web({ animation: 'sk-rise 260ms cubic-bezier(.2,.7,.2,1) both' }),
                      ]}
                    >
                      <View style={{ width: 26, height: 26, borderRadius: 13, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.card, borderWidth: 1, borderColor: st.border }}>
                        <Icon name={staleness.jobChanged ? 'alert-circle' : 'info'} size={13} color={st.fg} />
                      </View>
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <Text style={{ fontSize: fontSize.sm, color: colors.ink, fontWeight: fontWeight.semibold }}>
                          {staleness.jobChanged
                            ? `Out of date · scored on rev ${staleness.ranAgainstRevision}, now rev ${staleness.currentRevision}`
                            : staleness.updatedCandidateIds.length > 0
                              ? `${staleness.updatedCandidateIds.length} ranked candidate${staleness.updatedCandidateIds.length === 1 ? '' : 's'} changed since this run`
                              : `${staleness.newCandidateIds.length} new candidate${staleness.newCandidateIds.length === 1 ? '' : 's'} not yet scored`}
                        </Text>
                        <Text style={{ fontSize: fontSize.xs, color: colors.ink3, marginTop: 2 }}>
                          {staleness.jobChanged
                            ? `The criteria moved, so every score below is stale.${
                                pendingCount ? ` ${pendingCount} candidate${pendingCount === 1 ? '' : 's'} also changed — a re-run covers both.` : ''}`
                            : staleness.updatedCandidateIds.length > 0
                              ? 'Their stored score no longer matches their profile. Everyone else below is unaffected.'
                              : 'New to the pool, so this job has never scored them — scoring decides whether they fit.'}
                        </Text>
                      </View>
                      <ActionButton
                        label={staleness.jobChanged ? 'Re-run' : `Score ${pendingCount}`}
                        variant="secondary"
                        size="sm"
                        icon="refresh"
                        loading={rerunning}
                        onPress={() => void onRunMatch(!staleness.jobChanged)}
                      />
                    </View>
                  );
                })()}

                {match.topMatches.length === 0 ? (
                  <View style={{ alignItems: 'center', gap: 8, paddingVertical: 26 }}>
                    <View style={{ width: 40, height: 40, borderRadius: 20, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line }}>
                      <Icon name="sliders" size={17} color={colors.ink4} />
                    </View>
                    <Text style={{ fontSize: fontSize.sm, color: colors.ink2, fontWeight: fontWeight.medium }}>
                      No candidate cleared the gate
                    </Text>
                    <Text style={{ fontSize: fontSize.xs, color: colors.ink4, textAlign: 'center', maxWidth: 360, lineHeight: 17 }}>
                      Every candidate failed a must-have skill, the experience band or the location.
                      Open full results to see the reason per candidate.
                    </Text>
                  </View>
                ) : (
                  match.topMatches.map((m, i) => (
                    <MatchRow
                      key={m.candidateId}
                      rank={i + 1}
                      first={i === 0}
                      name={m.name}
                      score={m.score}
                      strong={m.isStrongMatch}
                      breakdown={m.breakdownString}
                      // This exact candidate changed after the run, so THEIR score
                      // is the one that cannot be trusted — mark the row rather
                      // than leaving the reader to guess which of the three it is.
                      // Only a candidate THIS ranking already holds can have a
                      // stale row; a never-scored one has no row to mark.
                      stale={!!staleness?.updatedCandidateIds.includes(m.candidateId)}
                      onOpen={() => navigation.navigate('candidateDetail', { id: m.candidateId })}
                    />
                  ))
                )}
              </View>
            )}
          </Panel>
        </View>

        {/* Source document — the original uploaded JD (PDF/CSV/Excel) in SK-Drive. */}
        {data.sourceFileId && (
          <View style={[{ flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 12, paddingHorizontal: 16, borderWidth: 1, borderColor: colors.line, borderRadius: radii.lg, backgroundColor: colors.card }, rise(140), web({ boxShadow: '0 1px 0 rgba(16,24,40,0.04)' })]}>
            <Pressable
              onPress={onDownloadSource}
              disabled={downloadingSource}
              accessibilityLabel="Download source document"
              style={[{ width: 38, height: 38, borderRadius: 10, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line }, web({ cursor: 'pointer', transition: 'background 140ms ease, border-color 140ms ease' })]}
            >
              {downloadingSource ? <ActivityIndicator size="small" color={colors.ink2} /> : <Icon name="download" size={18} color={colors.ink2} />}
            </Pressable>
            <View style={{ flex: 1, minWidth: 0 }}>
              {/* Mirrors the candidate panel's "Resume on file" — same idea, same
                  shape of words, so the two screens read as one product. */}
              <Text style={{ fontSize: fontSize.xs, color: colors.ink3, textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: fontWeight.semibold }}>Job description on file</Text>
              <Text style={{ fontSize: fontSize.sm, color: colors.ink, fontWeight: fontWeight.medium, marginTop: 2 }} numberOfLines={1}>{data.sourceFilename ?? 'Original job description'}</Text>
            </View>
            <ActionButton label="View" variant="secondary" size="sm" icon="eye" onPress={onViewSource} loading={loadingSource} />
          </View>
        )}
        </View>

        {/* Two columns: criteria | revision history */}
        <View style={[{ flexDirection: 'row', gap: 20 }, isMobile && { flexDirection: 'column' }]}>
          <View style={[{ flex: isMobile ? undefined : 1.4 }, rise(170)]}>
            <Panel
              title={`Matching criteria · immutable for rev ${rev.revisionNo}`}
              // Editing appends a NEW revision (the current one stays immutable),
              // which is what makes the stored ranking stale — so the match panel
              // above will offer a full re-run straight after saving.
              action={
                <ActionButton
                  label="Edit"
                  variant="secondary"
                  size="sm"
                  icon="edit"
                  onPress={() => setEditOpen(true)}
                />
              }
            >
              <View style={{ gap: 16 }}>
                <View style={{ gap: 8 }}>
                  <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontWeight: fontWeight.semibold, textTransform: 'uppercase', letterSpacing: 0.5 }}>Must-have</Text>
                  {rev.mustHaveSkills.length ? <SkillChips skills={rev.mustHaveSkills} max={12} tone="accent" /> : <Text style={{ fontSize: fontSize.sm, color: colors.ink4 }}>None specified</Text>}
                </View>
                <View style={{ gap: 8 }}>
                  <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontWeight: fontWeight.semibold, textTransform: 'uppercase', letterSpacing: 0.5 }}>Nice-to-have</Text>
                  {rev.niceToHaveSkills.length ? <SkillChips skills={rev.niceToHaveSkills} max={12} tone="neutral" /> : <Text style={{ fontSize: fontSize.sm, color: colors.ink4, fontStyle: 'italic' }}>None — Stage-3 backfills semantic fit to the JD.</Text>}
                </View>
                <View style={{ marginTop: 2 }}>
                  {META.map((m, i) => (
                    <View key={m.label} style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 13, borderBottomWidth: i === META.length - 1 ? 0 : 1, borderBottomColor: colors.line, gap: 16 }}>
                      <Text style={{ fontSize: fontSize.sm, color: colors.ink3 }}>{m.label}</Text>
                      <Text style={{ fontSize: fontSize.sm, color: colors.ink, fontWeight: fontWeight.semibold, flexShrink: 1, textAlign: 'right' }}>{m.value}</Text>
                    </View>
                  ))}
                </View>
              </View>
            </Panel>
          </View>

          <View style={[{ flex: isMobile ? undefined : 1 }, rise(230)]}>
            <Panel title="Revision history">
              <View style={{ gap: 0 }}>
                {data.revisions.map((r, i) => (
                  <RevRow
                    key={r.id}
                    revNo={r.revisionNo}
                    title={r.title}
                    dateLabel={fmtDate(r.createdAt)}
                    isCurrent={r.isCurrent}
                    isLast={i === data.revisions.length - 1}
                  />
                ))}
              </View>
            </Panel>
          </View>
        </View>

        {/* Pipeline kanban (projection of the append-only action stream) */}
        <View style={rise(290)}>
          <Panel
            title="Pipeline"
            /* Run match AND Close job, because this panel is where the decision is
               actually made: the recruiter moves the last candidate into Placed
               while looking at THIS board, and the very next thing they want is to
               end the job. Sending them up to the page header to do it — or worse,
               back to the Jobs list — puts navigation between an intent and its
               action. Closed jobs show Reopen here for the same reason. */
            action={
              data.status === 'closed' ? (
                <ActionButton label="Reopen job" variant="secondary" size="sm" icon="refresh" onPress={() => setConfirmReopen(true)} loading={busy} />
              ) : data.status === 'active' || data.status === 'on_hold' ? (
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  {data.status === 'active' && (
                    <ActionButton label="Run match" variant="primary" size="sm" icon="play" onPress={() => navigation.navigate('run', { requisitionId: id, autoRun: Date.now() })} />
                  )}
                  <ActionButton label="Close job" variant="secondary" size="sm" icon="check" onPress={() => setConfirmClose(true)} loading={busy} />
                </View>
              ) : undefined
            }
            flush
          >
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={isMobile}
              {...(isMobile ? ({ dataSet: { skScroll: 'x' } } as object) : {})}
              contentContainerStyle={{ padding: 14, paddingBottom: isMobile ? 16 : 14, gap: 12 }}
            >
              {LANES.map((lane) => {
                const items = pipeline?.lanes?.[lane.key] ?? [];
                return (
                  <View key={lane.key} style={{ width: 184, backgroundColor: colors.paper2, borderRadius: 12, borderWidth: 1, borderColor: colors.line, padding: 10, gap: 10 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7 }}>
                      <View style={{ width: 8, height: 8, borderRadius: 999, backgroundColor: colors[lane.tone] }} />
                      <Text style={{ fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: colors.ink2, flex: 1 }}>{lane.label}</Text>
                      <View style={{ minWidth: 18, height: 18, paddingHorizontal: 5, alignItems: 'center', justifyContent: 'center', borderRadius: 9, backgroundColor: colors.paper3 }}>
                        <Text style={{ fontFamily: font.mono, fontSize: 10, color: colors.ink3, fontWeight: fontWeight.semibold }}>{items.length}</Text>
                      </View>
                    </View>
                    {items.map((it) => {
                      const next = NEXT_STAGE[lane.key];
                      const terminal = lane.key === 'placed' || lane.key === 'closed';
                      return (
                        <PipeCard
                          key={it.candidateId}
                          name={it.name}
                          terminal={terminal}
                          next={next}
                          moving={movingId === it.candidateId}
                          onOpen={() => navigation.navigate('candidateDetail', { id: it.candidateId })}
                          onAdvance={() => next && setPendingAdvance({ candidateId: it.candidateId, name: it.name, next, nextLabel: ADVANCE_LABEL[next] ?? 'the next stage' })}
                          onReject={() => setReasonFor({ candidateId: it.candidateId, action: 'rejected' })}
                          onWithdraw={() => setReasonFor({ candidateId: it.candidateId, action: 'withdrawn' })}
                        />
                      );
                    })}
                    {items.length === 0 && (
                      <View style={{ paddingVertical: 16, alignItems: 'center' }}>
                        <Text style={{ fontSize: 11, color: colors.ink4 }}>Empty</Text>
                      </View>
                    )}
                  </View>
                );
              })}
            </ScrollView>
          </Panel>
        </View>
      </View>

      {/* Edit → appends a revision. Reloading picks up the new criteria AND the
          staleness verdict, so the match panel immediately shows it is out of date. */}
      <NewJobModal
        open={editOpen}
        editing={data}
        onClose={() => setEditOpen(false)}
        onCreated={() => setEditOpen(false)}
        onUpdated={() => {
          setEditOpen(false);
          load();
          // Only worth asking when there IS a ranking to invalidate — a job that
          // has never been matched has nothing to re-run.
          if (match) setAskRerun(true);
        }}
      />

      <ReasonModal
        open={!!reasonFor}
        title={reasonFor?.action === 'withdrawn' ? 'Withdraw candidate' : 'Reject candidate'}
        onClose={() => setReasonFor(null)}
        onSubmit={submitReason}
      />

      {/* Activate → confirm before embedding the JD. */}
      {/* Job updated → offer the re-run straight away. A revision changes the
          criteria, so EVERY stored score is stale — including for candidates who
          did not change. That is why this is a full re-run and not the cheap
          incremental path used when candidates are added. */}
      <ConfirmDialog
        visible={askRerun}
        title="Job updated — re-run the match?"
        message={
          `The matching criteria changed, so the ${match?.rankedCount ?? 0} score${(match?.rankedCount ?? 0) === 1 ? '' : 's'} `
          + `from the last run no longer reflect this job. A re-run rescores the whole pool against the new criteria; `
          + `until then the results shown are the old ones. You can also do this later from the Matches panel.`
        }
        confirmText="Re-run now"
        cancelText="Later"
        busy={rerunning}
        onConfirm={async () => { await onRunMatch(false); setAskRerun(false); }}
        onCancel={() => setAskRerun(false)}
      />

      <ConfirmDialog
        visible={confirmClose}
        title="Close this job?"
        message={`“${rev.title}” will stop matching and drop off the active list. Nothing is deleted — its revisions, pipeline and match results are kept, and you can reopen it later.`}
        confirmText="Close job"
        cancelText="Keep open"
        confirmColor={colors.ink}
        busy={busy}
        onConfirm={onClose}
        onCancel={() => setConfirmClose(false)}
      />

      <ConfirmDialog
        visible={confirmReopen}
        title="Reopen this job?"
        message={`Put “${rev.title}” back to active so it can be matched again. Nothing is lost — its revisions, pipeline and past match runs stay exactly as they are. This uses one of your active-opening slots.`}
        confirmText="Reopen"
        cancelText="Keep closed"
        confirmColor={colors.accent}
        busy={busy}
        onConfirm={onReopen}
        onCancel={() => setConfirmReopen(false)}
      />

      <ConfirmDialog
        visible={confirmActivate}
        title="Activate this job?"
        message={`This embeds “${rev.title}”'s JD (rev ${rev.revisionNo}) and opens the job for matching. A draft can't be edited back out once active — you'd add a new revision instead.`}
        confirmText="Activate"
        cancelText="Not yet"
        confirmColor={colors.success}
        busy={busy}
        onConfirm={onActivate}
        onCancel={() => setConfirmActivate(false)}
      />

      {/* Advance → confirm the pipeline move (append-only). */}
      <ConfirmDialog
        visible={!!pendingAdvance}
        title={pendingAdvance?.next === 'placed' ? 'Mark as placed?' : `Move to ${pendingAdvance?.nextLabel ?? ''}?`}
        message={
          pendingAdvance?.next === 'placed'
            ? `Mark ${pendingAdvance?.name} as Placed for this job? This is a terminal stage — it records the placement in the append-only history.`
            : `Move ${pendingAdvance?.name} to ${pendingAdvance?.nextLabel}? This records a stage change in the pipeline.`
        }
        confirmText={pendingAdvance?.next === 'placed' ? 'Mark placed' : `Move`}
        cancelText="Cancel"
        confirmColor={pendingAdvance?.next === 'placed' ? colors.success : colors.accent}
        busy={movingId === pendingAdvance?.candidateId}
        onConfirm={doAdvance}
        onCancel={() => setPendingAdvance(null)}
      />

      {/* Activate → success moment. */}
      <SuccessDialog
        visible={activated}
        title="Job activated"
        message={`“${rev.title}” is now active with its JD embedded — you can run matches on it.`}
        primaryAction={{ label: 'Run match', onPress: () => { setActivated(false); navigation.navigate('run', { requisitionId: id, autoRun: Date.now() }); } }}
        secondaryAction={{ label: 'Done', onPress: () => setActivated(false) }}
        onClose={() => setActivated(false)}
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  loading: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  header: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 },
});

/**
 * One ranked candidate. Split out so it can own a hover state — a flat list of
 * Pressables with no affordance does not read as clickable, and the score is the
 * thing the eye should land on, so it gets the weight rather than the rank.
 */
function MatchRow({
  rank, first, name, score, strong, breakdown, stale, onOpen,
}: {
  rank: number; first: boolean; name: string; score: number;
  strong: boolean; breakdown: string; stale?: boolean; onOpen: () => void;
}) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font, radii } = theme;
  const { hovered, hoverProps } = useHover();
  // Top three earn a filled medallion; the rest stay quiet numerals so the
  // shortlist is legible at a glance instead of every row competing.
  const podium = rank <= 3;
  return (
    <Pressable
      {...hoverProps}
      onPress={onOpen}
      style={[
        {
          flexDirection: 'row', alignItems: 'center', gap: 12,
          paddingVertical: 11, paddingHorizontal: 8, marginHorizontal: -8,
          borderRadius: radii.md,
          borderTopWidth: first ? 0 : 1, borderTopColor: colors.line,
        },
        web({ cursor: 'pointer', transition: 'background 140ms ease' }),
        stale && {
          backgroundColor: colors.paper2,
          borderLeftWidth: 3,
          borderLeftColor: colors.ink,
          paddingLeft: 9,
          borderTopColor: 'transparent',
        },
        hovered && { backgroundColor: colors.paper3 ?? colors.paper2, borderTopColor: 'transparent' },
      ]}
    >
      <View
        style={{
          width: 22, height: 22, borderRadius: 11, alignItems: 'center', justifyContent: 'center',
          backgroundColor: podium ? colors.ink : 'transparent',
          borderWidth: podium ? 0 : 1, borderColor: colors.line,
        }}
      >
        <Text style={{ fontFamily: font.mono, fontSize: 10, fontWeight: fontWeight.semibold, color: podium ? colors.paper : colors.ink4 }}>{rank}</Text>
      </View>
      <MiniAvatar name={name} size={32} />
      <View style={{ flex: 1, minWidth: 0 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <Text numberOfLines={1} style={{ fontSize: fontSize.md, color: colors.ink, fontWeight: fontWeight.semibold, letterSpacing: -0.2 }}>{name}</Text>
          {strong && <StatusChip tone="success" label="Strong" />}
          {/* Deliberately the strongest thing on the row: in a grayscale system
              emphasis is contrast, so this inverts to ink-on-paper rather than
              being another outlined grey chip that disappears into the others. */}
          {stale && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5, paddingVertical: 3, paddingHorizontal: 8, borderRadius: 999, backgroundColor: colors.ink }}>
              <Icon name="refresh" size={10} color={colors.paper} />
              <Text style={{ fontSize: 10, color: colors.paper, fontWeight: fontWeight.bold, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                Re-run needed
              </Text>
            </View>
          )}
        </View>
        <Text
          numberOfLines={1}
          style={{
            fontSize: fontSize.xs, marginTop: 3,
            color: stale ? colors.ink2 : colors.ink4,
            fontWeight: stale ? fontWeight.medium : undefined,
            fontFamily: stale ? undefined : font.mono,
          }}
        >
          {stale ? 'This profile changed after the score below — re-run to update it.' : skillsOnlyBreakdown(breakdown)}
        </Text>
      </View>
      <View style={{ alignItems: 'flex-end' }}>
        {/* A score whose candidate has since changed is shown muted: it is the
            real stored value, but it no longer describes this profile. */}
        <Text style={{ fontFamily: font.mono, fontSize: 19, color: stale ? colors.ink4 : colors.ink, fontWeight: fontWeight.bold, letterSpacing: -0.5 }}>{score}</Text>
        <Text style={{ fontSize: 10, color: colors.ink4, marginTop: -2 }}>/ 100</Text>
      </View>
      <Icon name="arrow-r" size={14} color={hovered ? colors.ink3 : colors.ink4} />
    </Pressable>
  );
}

/** "Out of date" pill. One component, two placements (header + Matches card), so
 *  the wording and tone stay identical wherever it appears. */
function StaleTag({ label, tone, style }: { label: string; tone: 'warn' | 'accent'; style?: object }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight } = theme;
  const t = toneColors(colors, tone);
  // The job-level problem (warn) inverts to ink-on-paper — in a grayscale system
  // that is the only way to say "act on this" louder than the surrounding chips.
  // The candidate-level one stays outlined: it is additive, not invalidating.
  const filled = tone === 'warn';
  return (
    <View
      style={[
        {
          flexDirection: 'row', alignItems: 'center', gap: 6,
          paddingVertical: 5, paddingHorizontal: 10, borderRadius: 999,
          backgroundColor: filled ? colors.ink : t.bg,
          borderWidth: 1, borderColor: filled ? colors.ink : t.border,
        },
        web({ animation: 'sk-rise 260ms ease both' }),
        style as object,
      ]}
    >
      <Icon name={filled ? 'refresh' : 'alert-circle'} size={11} color={filled ? colors.paper : t.fg} />
      <Text
        style={{
          fontSize: fontSize.xs,
          color: filled ? colors.paper : t.fg,
          fontWeight: fontWeight.bold,
          letterSpacing: filled ? 0.2 : 0,
        }}
      >
        {label}
      </Text>
    </View>
  );
}
